/**
 * Errores de los envíos por código: barras horizontales con los 10 principales y la tabla
 * código · qué pasó · qué hacer · cantidad · «Ver en Twilio». Los títulos y acciones los arma el
 * backend (`codigos.util.ts`); acá no se interpreta ningún código.
 *
 * «Ver en Twilio» es del equipo: una persona de un cliente (Accesos, rol «comercio») lee la tarjeta en el
 * «Detalle técnico» de Estadísticas y no sabe qué es Twilio (el link lleva a su documentación de errores).
 * Tampoco los códigos: ve el motivo en palabras (`motivoFalloParaCliente`), sin la columna «Código» ni las barras,
 * que se rotulan por código; la tabla ya va ordenada por cantidad.
 */

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CheckCircle2, ExternalLink } from "lucide-react";
import type { ErrorPorCodigo } from "../../types.estadisticas";
import { COLORES } from "../../logic/estadisticas";
import { num } from "../../../campana-nueva/utils/format";
import { ESTILO_TOOLTIP } from "./comun";
import { accionFalloParaCliente, motivoFalloParaCliente } from "../../logic/textos";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

export function TarjetaErrores({ errores }: { errores: ErrorPorCodigo[] | null | undefined }) {
  const equipo = useEsEquipoDeenex();
  const filas = [...(errores || [])].sort((a, b) => (Number(b.cantidad) || 0) - (Number(a.cantidad) || 0));
  if (!filas.length) {
    return (
      <p className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
        <CheckCircle2 size={15} className="text-emerald-500" /> Ningún mensaje falló en estas fechas.
      </p>
    );
  }
  const top = filas.slice(0, 10).map((f) => ({ codigo: f.codigo, cantidad: Number(f.cantidad) || 0, titulo: f.titulo }));
  return (
    <div className="space-y-4">
      {equipo && (
      <div style={{ width: "100%", height: Math.max(120, top.length * 28 + 20) }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={top} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={COLORES.grilla} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: COLORES.eje }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="codigo" width={56} tick={{ fontSize: 11, fill: "#475569" }} tickLine={false} axisLine={false} />
            <Tooltip {...ESTILO_TOOLTIP} formatter={(v) => [Number(v).toLocaleString("es-AR"), "Mensajes"]}
              labelFormatter={(l) => { const f = top.find((x) => x.codigo === l); return f ? `${l} · ${f.titulo}` : String(l); }} />
            <Bar dataKey="cantidad" name="Mensajes" fill={COLORES.fallidos} radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
              {equipo && <th scope="col" className="py-2 pr-3">Código</th>}
              <th scope="col" className="py-2 pr-3">Qué pasó</th>
              <th scope="col" className="py-2 pr-3">Qué hacer</th>
              <th scope="col" className="py-2 pr-3 text-right">Cantidad</th>
              <th scope="col" className="py-2"><span className="sr-only">Documentación</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filas.map((f) => (
              <tr key={f.codigo} className="align-top">
                {equipo && <td className="py-2.5 pr-3 font-mono text-xs text-slate-500">{f.codigo}</td>}
                <td className="py-2.5 pr-3 text-slate-800">{equipo ? f.titulo : motivoFalloParaCliente(f.titulo)}</td>
                <td className="py-2.5 pr-3 text-slate-600">{equipo ? f.accionTexto : accionFalloParaCliente(f.accionTexto)}</td>
                <td className="py-2.5 pr-3 text-right font-semibold text-slate-900 tabular-nums">{num(f.cantidad)}</td>
                <td className="py-2.5 text-right">
                  {equipo && f.urlDoc && (
                    <a href={f.urlDoc} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline whitespace-nowrap">
                      Ver en Twilio <ExternalLink size={11} />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TarjetaErrores;
